"use client";
import React, { useState } from "react";

import type { BlockProps, StrapiBlock } from "./blocks/types";
import { Heading } from "../design-system/atoms/Heading";
import Icon from "../design-system/atoms/Icon";
import { ChevronLeft, ChevronRight } from "lucide-react";

type Media = {
  id?: number;
  documentId?: string;
  url?: string | null;
  alternativeText?: string | null;
};

type Cta = {
  id?: number;
  text?: string | null;
  link?: string | null;
};

type ChoixMenu = {
  id?: number;
  documentId?: string;
  titre?: string | null;
  descr?: string | null;
  icon?: Media | null;
  iconHover?: Media | null;
  cta?: Cta[];
};

type InfoMenusBlock = StrapiBlock & {
  __component: "blocks.info-menus";
  title?: string | null;
  variant?: string | null;
  choixMenus?: ChoixMenu[];
};

function MenuIcon({ item, featured = false }: { item: ChoixMenu; featured?: boolean }) {
  const iconUrl = item.icon?.url || "";
  const iconHoverUrl = item.iconHover?.url || "";
  const iconAlt = item.icon?.alternativeText || item.titre || "Service";

  return (
    <div
      className="relative z-10 flex h-24 w-24 items-center justify-center rounded-full border-[3px] border-primaryGreen "
    >
      {iconUrl && (
        <Icon
          src={iconUrl}
          alt={iconAlt}
          className={`h-24 w-24 object-contain transition-opacity duration-300 ${
            iconHoverUrl ? "group-hover:opacity-0" : ""
          }`}
        />
      )}

      {iconHoverUrl && (
        <Icon
          src={iconHoverUrl}
          alt={item.iconHover?.alternativeText || iconAlt}
          className="absolute h-16 w-16 object-contain opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        />
      )}
    </div>
  );
}

export default function PourquoiLMS({ data }: BlockProps<InfoMenusBlock>) {
  if (!data) return null;

  const choixMenus = Array.isArray(data.choixMenus) ? data.choixMenus : [];

  if (data.variant === "prescripteur") {
    return (
      <section className="bg-white py-24">
        <div className="lms-container text-center">
          {data.title && (
            <Heading as="h2" className="mb-16 text-center text-3xl font-bold text-text lg:text-5xl">
              {(() => {
                const words = data.title.split(" ");
                const highlightedPart = words.slice(-4).join(" ");
                const firstPart = words.slice(0, -4).join(" ");

                return (
                  <>
                    {firstPart}{" "}
                    <span className="font-semibold text-secondary">
                      {highlightedPart}
                    </span>
                  </>
                );
              })()}
            </Heading>
          )}

          <div className="grid grid-cols-2 gap-10 pt-14 sm:grid-cols-2 lg:grid-cols-4 lg:gap-12">
            {choixMenus.map((item, index) => (
              <div
                key={item.documentId ?? item.id ?? index}
                className="group flex flex-col items-center space-y-4 p-5 text-center transition hover:rounded-2xl hover:border-gray-200 hover:shadow-[0_8px_30px_rgba(0,0,0,0.1)]"
              >
                <div className="relative flex h-20 w-20 items-center justify-center transition-transform duration-300 group-hover:scale-110">
                  {item.icon?.url && (
                    <Icon
                      src={item.icon.url}
                      alt={item.icon.alternativeText || item.titre || "Service"}
                      className="h-full w-full object-contain"
                    />
                  )}
                </div>

                <div className="h-1 w-20 rounded-full bg-primaryforeground" />

                {item.descr && (
                  <p className="max-w-[250px] text-base leading-snug text-gray-700 lg:text-lg">
                    {item.descr}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

if (data.variant === "agence") {
  const [activeIndex, setActiveIndex] = useState(0);

  const previous = () => {
    setActiveIndex((prev) =>
      prev === 0 ? choixMenus.length - 1 : prev - 1
    );
  };

  const next = () => {
    setActiveIndex((prev) =>
      prev === choixMenus.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <section className="bg-white py-6 lg:py-12">
      <div className="lms-container">
        {data.title && (
          <Heading as="h2" className="mb-10 text-center font-playfair text-3xl font-bold text-textColor lg:mb-16 lg:text-[44px]">
            {data.title}
          </Heading>
        )}

        {/* ================= MOBILE SLIDER ================= */}
        <div className="overflow-hidden lg:hidden">
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${activeIndex * 100}%)` }}
          >
            {choixMenus.map((item, index) => {
              const featured = index === 1;

              return (
                <div
                  key={item.documentId ?? item.id ?? index}
                  className="w-full shrink-0 px-2"
                >
                  <article className="group flex flex-col items-center text-center">
                    <MenuIcon item={item} featured={featured} />

                    <div className="-mt-12 flex min-h-[260px] w-full max-w-[330px] flex-col items-center rounded-[18px] bg-white px-8 pb-8 pt-20 shadow-[0_10px_30px_rgba(0,0,0,0.10)]">
                      {item.titre && (
                        <h3 className="max-w-[260px] text-center text-[20px] font-bold leading-[1.05] text-[#343434]">
                          {item.titre}
                        </h3>
                      )}

                      <div className="my-6 h-[3px] w-20 rounded-full bg-gradient-to-r from-greenPastel via-primaryGreen to-greenPastel" />

                      {item.descr && (
                        <p className="max-w-[280px] whitespace-pre-line text-[16px] leading-[1.2] text-textColor">
                          {item.descr}
                        </p>
                      )}
                    </div>
                  </article>
                </div>
              );
            })}
          </div>

          {/* NAVIGATION */}
          {choixMenus.length > 1 && (
            <div className="mt-7 flex items-center justify-center gap-5">
              <button
                type="button"
                onClick={previous}
                aria-label="Élément précédent"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-primaryGreen text-primaryGreen transition-colors hover:bg-primaryGreen hover:text-white"
              >
                <ChevronLeft size={20} />
              </button>

              <div className="flex items-center gap-2">
                {choixMenus.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    aria-label={`Afficher l'élément ${index + 1}`}
                    className={`h-2.5 rounded-full transition-all duration-300 ${
                      activeIndex === index
                        ? "w-7 bg-primaryGreen"
                        : "w-2.5 bg-greenPastel"
                    }`}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={next}
                aria-label="Élément suivant"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-primaryGreen text-primaryGreen transition-colors hover:bg-primaryGreen hover:text-white"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>

        {/* ================= DESKTOP ================= */}
        <div className="mx-auto hidden max-w-[1100px] items-start gap-16 lg:grid lg:grid-cols-3 lg:gap-14">
          {choixMenus.map((item, index) => {
            const featured = index === 1;

            return (
              <article
                key={item.documentId ?? item.id ?? index}
                className="group flex h-full flex-col items-center text-center"
              >
                <MenuIcon item={item} featured={featured} />

                <div className="-mt-12 flex min-h-[280px] w-full max-w-[330px] flex-col items-center bg-white px-8 pb-10 pt-20 transition-all duration-300 hover:rounded-[18px] hover:shadow-[0_14px_40px_rgba(0,0,0,0.12)]">
                  {item.titre && (
                    <h3 className="max-w-[260px] text-center text-[20px] font-bold leading-[1.05] text-[#343434]">
                      {item.titre}
                    </h3>
                  )}

                  <div className="my-6 h-[3px] w-20 rounded-full bg-gradient-to-r from-greenPastel via-primaryGreen to-greenPastel" />

                  {item.descr && (
                    <p className="max-w-[280px] whitespace-pre-line text-[16px] leading-[1.15] text-textColor">
                      {item.descr}
                    </p>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

  return (
    <section className="flex justify-center bg-accent lg:-mt-[200px] lg:min-h-[65vh] lg:items-center">
      <div className="lms-container flex flex-col items-center pb-8 pt-5 lg:pt-24">
        <Heading as="h2" className="pt-6 text-center text-4xl font-bold text-text lg:text-5xl 2xl:pt-20 3xl:pt-10">
          {data.title ? (
            <>
              {data.title.split(" ").slice(0, -3).join(" ")}{" "}
              <span className="text-white">
                {data.title.split(" ").slice(-3).join(" ")}
              </span>
            </>
          ) : (
            "Pourquoi choisir Les Menus Services ?"
          )}
        </Heading>

        <div className="flex max-w-7xl flex-col gap-6 lg:flex-row">
          {choixMenus.map((item, index) => (
            <article
              key={item.documentId ?? item.id ?? index}
              className="flex-1 rounded-2xl bg-white px-4 py-6 text-center shadow-sm lg:shadow-[0_0_29.8px_rgba(0,0,0,0.15)]"
            >
              {item.icon?.url && (
                <Icon
                  src={item.icon.url}
                  alt={item.icon.alternativeText || item.titre || "Service"}
                  className="mx-auto h-20 w-20 object-contain"
                />
              )}

              {item.titre && (
                <h3 className="mt-6 text-2xl font-semibold text-primaryGreen">
                  {item.titre}
                </h3>
              )}

              {item.descr && (
                <p className="mt-4 text-base leading-relaxed text-text">
                  {item.descr}
                </p>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}